import { OptionInfoDto } from "../../../screens/options-table/types/OptionInfoDto";
import { OrderModel } from "../../../screens/portfolio-info/types/OrderModel";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import {
    optionParametersSelectorManager
} from "../../../screens/positions-and-orders/utils/OptionParametersSelectorManager";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { BlackScholesModelCalculationService } from "../BlackScholesModelCalculationService";
import { dateTimeConversionService } from "../DateTimeConversionService";
import { OrderState } from "../../types/OrderState";
import type { MarketDataDto } from "../../types/MarketDataDto";
import { assetService } from "../AssetService";
import { portfolioModelService } from "../portfolio-model-service/PortfolioModelService";
import { TargetPlatform } from "../../types/TargetPlatform";
import { platformFeeCalculationService } from "../platform-fee-calculation-service/PlatformFeeCalculationService";
import { OrderProcessingType } from "../../types/OrderProcessingType";

export type PositionCustomImpliedVolatilityUpdate = {
    targetPlatform: TargetPlatform;
    customImpliedVolatility: number;
    customImpliedVolatilityStep?: number | null;
};

class OrdersService {
    recalculateFee(order: OrderModel): boolean {
        if (order.processingType === OrderProcessingType.Manual ||
            !platformFeeCalculationService.supports(order.targetPlatform)) {
            return false;
        }

        const fee = platformFeeCalculationService.calculate({
            targetPlatform: order.targetPlatform,
            assetType: order.type,
            processingType: order.processingType,
            size: order.size,
            price: order.openPrice,
            underlyingPrice: order.underlyingPriceAtOpen,
        });

        if (fee === null) {
            return false;
        }

        order.fee = fee === 0 ? 0 : -fee;
        return true;
    }

    createOptionOrder(
        order: OptionInfoDto,
        size: number,
        underlyingPrice: number,
    ): OrderModel {
        return new OrderModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Disabled,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: order.ExpireAt,
            impliedVolatilityAtOpen: order.MarkImpliedVolatility,
            customImpliedVolatility: order.MarkImpliedVolatility,
            symbol: order.Symbol,
            size,
            openPrice: order.MarkPrice,
            markPrice: order.MarkPrice,
            markImpliedVolatility: order.MarkImpliedVolatility,
            type: order.Type,
            strike: order.Strike,
            underlyingPriceAtOpen: underlyingPrice,
            delta: order.Delta,
            gamma: order.Gamma,
            vega: order.Vega,
            theta: order.Theta,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: order.MarkImpliedVolatility,
            underlyingPriceForPricing: underlyingPrice,
        });
    }

    addOrder(portfolio: PortfolioModel, order: OrderModel): PortfolioModel {
        return this.addOrders(portfolio, [order]);
    }

    addOrders(portfolio: PortfolioModel, orders: OrderModel[]): PortfolioModel {
        return portfolioModelService.copyPortfolio(portfolio, {
            orders: [...portfolio.orders, ...orders],
            modifiedAt: new Date(),
        });
    }

    deleteOrder(portfolio: PortfolioModel, internalId: string): PortfolioModel {
        return portfolioModelService.copyPortfolio(portfolio, {
            orders: portfolio.orders.filter(order => order.internalId !== internalId),
            modifiedAt: new Date(),
        });
    }

    updateOptionCustomImpliedVolatility(
        portfolio: PortfolioModel,
        optionSymbol: string,
        targetPlatform: TargetPlatform,
        customImpliedVolatility: number,
        customImpliedVolatilityStep?: number | null,
    ): PortfolioModel {
        return this.updateOptionCustomImpliedVolatilities(
            portfolio,
            optionSymbol,
            [{
                targetPlatform,
                customImpliedVolatility,
                customImpliedVolatilityStep,
            }],
        );
    }

    updateOptionCustomImpliedVolatilities(
        portfolio: PortfolioModel,
        optionSymbol: string,
        updates: PositionCustomImpliedVolatilityUpdate[],
    ): PortfolioModel {
        const updatesByPlatform = new Map(
            updates.map(update => [update.targetPlatform, update]),
        );

        return portfolioModelService.copyPortfolio(portfolio, {
            positions: portfolio.positions.map(position => {
                const update = updatesByPlatform.get(position.targetPlatform);

                if (!assetService.isOptionType(position.type) ||
                    position.symbol !== optionSymbol ||
                    update === undefined) {
                    return portfolioModelService.copyPosition(position);
                }

                return portfolioModelService.copyPosition(position, {
                    customImpliedVolatility: update.customImpliedVolatility,
                    customImpliedVolatilityStep: update.customImpliedVolatilityStep !== undefined
                        ? update.customImpliedVolatilityStep
                        : position.customImpliedVolatilityStep,
                });
            }),
            orders: portfolio.orders.map(order => {
                const update = updatesByPlatform.get(order.targetPlatform);

                if (!assetService.isOptionType(order.type) ||
                    order.symbol !== optionSymbol ||
                    update === undefined) {
                    return portfolioModelService.copyOrder(order);
                }

                return portfolioModelService.copyOrder(order, {
                    customImpliedVolatility: update.customImpliedVolatility,
                    customImpliedVolatilityStep: update.customImpliedVolatilityStep !== undefined
                        ? update.customImpliedVolatilityStep
                        : order.customImpliedVolatilityStep,
                });
            }),
            modifiedAt: new Date(),
        });
    }

    refreshOrdersParameters(
        portfolio: PortfolioModel,
        marketData: MarketDataDto,
    ): PortfolioModel {
        return portfolioModelService.copyPortfolio(portfolio, {
            orders: portfolio.orders.map(order =>
                this.refreshOrderParameters(order, marketData)),
            modifiedAt: new Date(),
        });
    }

    private refreshOrderParameters(
        order: OrderModel,
        marketData: MarketDataDto,
    ): OrderModel {
        const optionInfoDto = optionParametersSelectorManager.getOption(
            marketData.OptionInfos,
            order.type,
            order.expireAt!,
            order.strike!,
        );

        const markImpliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? 0;
        const riskFreeInterestValue = optionInfoDto?.RiskFreeInterest
            ?? BlackScholesModelCalculationService.riskFreeRate;

        const { price, delta, gamma, vega, theta } =
            portfolioMappers.getAssetParameters(
                order.type,
                order.strike,
                order.expireAt,
                markImpliedVolatility,
                riskFreeInterestValue,
                marketData.UnderlyingPrice,
            );

        return portfolioModelService.copyOrder(order, {
            markImpliedVolatility,
            markPrice: price,
            delta: delta * order.size,
            gamma: (gamma ?? 0) * order.size,
            vega: (vega ?? 0) * order.size,
            theta: (theta ?? 0) * order.size,
        });
    }
}

export const ordersService = new OrdersService();
